import { useState, useMemo } from 'react';
import { useParams, useSearch, useNavigate } from '@tanstack/react-router';
import { useTenant, useExtensions, useCdrStats } from '../../lib/api/hooks.js';
import { useTenantTrunks } from '../../lib/api/hooks-products.js';
import {
  useRingGroups, useQueues, useIvrs, useNumbers, useTenantUsers,
} from '../../lib/api/hooks-tms.js';
import { Icon, BrandMark } from '../../components/ui/Icon.js';
import { StatusBadge, ErrorBanner, Stat, Empty } from '../../components/ui/Primitives.js';
import { useSession } from '../../lib/auth/session.js';
import { formatMoney, formatNumber } from '../../lib/format.js';
import { CONFIG_NAV, groupFor } from './config-nav.js';

import { ExtensionsPage } from '../extensions/ExtensionsPage.js';
import { CdrsPage } from '../cdrs/CdrsPage.js';
import { BillingPage } from '../billing/BillingPage.js';
import { AccountTrunksTab } from '../accounts/AccountTrunksTab.js';
import { RingGroupsTab } from './RingGroupsTab.js';
import { QueuesTab } from './QueuesTab.js';
import { IvrsTab } from './IvrsTab.js';
import { SchedulesTab } from './SchedulesTab.js';
import { DevicesTab } from './DevicesTab.js';
import { RecordingTab } from './RecordingTab.js';
import { DialPolicyTab } from './DialPolicyTab.js';
import { AuditTab } from './AuditTab.js';
import { VoicemailTab } from './VoicemailTab.js';
import { ForwardingTab } from './ForwardingTab.js';
import { NumbersTab } from './NumbersTab.js';
import { UsersTab } from './UsersTab.js';
import { AccountDashboard } from './AccountDashboard.js';

/**
 * Full account configuration, in its own browser tab.
 *
 * Its own SIDEBAR rather than the console's: this is a working session on one
 * customer with a deep feature tree, and a top tab strip cannot carry it
 * without either truncating or wrapping. The customer is named in the header
 * and in the URL, so with three of these open there is never a question of
 * which account is being edited.
 */
export function ManageAccountPage() {
  const { tenantId } = useParams({ from: '/manage/$tenantId' });
  const nav = useNavigate();
  /**
   * The open section lives in the URL, not in component state.
   *
   * Doc 07 says every view encodes its state in the URL so support can ask for
   * a link and see exactly what the customer sees. This screen did not: it held
   * the section in useState, so every link to it opened on the dashboard no
   * matter what the sender was looking at, and the platform Live view could not
   * deep-link into a seat at all.
   */
  const search = useSearch({ strict: false }) as { section?: string };
  const section = search.section ?? 'dashboard';
  const can = useSession((s) => s.can);
  const [open, setOpen] = useState<Set<string>>(() => {
    const initial = new Set(['overview', 'people', 'routing']);
    const g = groupFor(new URLSearchParams(window.location.search).get('section') ?? '');
    if (g) initial.add(g);
    return initial;
  });

  const account = useTenant(tenantId);
  const t = account.data;

  // Counts shown as pills. Cheap because every one of these is already
  // fetched by a tab the operator is likely to open anyway.
  const counts = {
    extensions: useExtensions(tenantId, { limit: 200 }).data?.data.length,
    ringGroups: useRingGroups(tenantId).data?.data.length,
    queues: useQueues(tenantId).data?.data.length,
    ivrs: useIvrs(tenantId).data?.data.length,
    numbers: useNumbers(tenantId).data?.data.length,
    trunks: useTenantTrunks(tenantId).data?.data.length,
    users: useTenantUsers(tenantId).data?.data.length,
  };

  const groups = useMemo(
    () => CONFIG_NAV
      .map((g) => ({ ...g, items: g.items.filter((i) => !i.permission || can(i.permission)) }))
      .filter((g) => g.items.length > 0),
    [can],
  );

  const active = groups.flatMap((g) => g.items).find((i) => i.key === section);

  const toggleGroup = (key: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });

  /** Switches to the extensions list AND names the seat, in one navigation. */
  const openExtension = (id: string) => {
    void nav({
      to: '.',
      search: (prev: Record<string, unknown>) => ({ ...prev, section: 'extensions', ext: id }),
    });
  };

  const go = (key: string) => {
    // `replace` so clicking through eight config sections does not bury the
    // page the operator arrived from under eight history entries.
    void nav({
      to: '.',
      search: (prev: Record<string, unknown>) => {
        const next: Record<string, unknown> = { ...prev, section: key };
        delete next['ext'];
        return next;
      },
      replace: true,
    });
    const g = groupFor(key);
    if (g) setOpen((prev) => new Set(prev).add(g));
  };

  return (
    <div className="arc-app">
      <aside className="arc-sidebar" style={{ ['--arc-sidebar-w' as string]: '244px' }}>
        <div className="arc-brand">
          <BrandMark size={22} />
          <div className="arc-stack">
            <span className="arc-truncate" style={{ maxWidth: 160 }}>{t?.name ?? '\u2026'}</span>
            <span className="arc-brand-sub">Configuration</span>
          </div>
        </div>

        <nav className="arc-nav" aria-label="Account configuration">
          {groups.map((g) => {
            const isOpen = open.has(g.key);
            const hasActive = g.items.some((i) => i.key === section);
            return (
              <div key={g.key}>
                <button
                  type="button"
                  className="arc-nav-item arc-nav-group"
                  onClick={() => toggleGroup(g.key)}
                  aria-expanded={isOpen}
                  style={{
                    width: '100%', border: 'none', background: 'none', font: 'inherit',
                    color: hasActive && !isOpen ? 'var(--arc-nav-fg-strong)' : undefined,
                  }}
                >
                  <Icon name={g.icon} size={18} />
                  <span style={{ flex: 1, textAlign: 'left' }}>{g.label}</span>
                  <Icon name={isOpen ? 'chevronDown' : 'chevronRight'} size={12} />
                </button>

                {isOpen && (
                  <div className="arc-nav-sublist">
                    {g.items.map((i) => (
                      <button
                        key={i.key}
                        type="button"
                        className="arc-nav-item arc-nav-sub"
                        data-active={section === i.key}
                        onClick={() => go(i.key)}
                      >
                        <span style={{ flex: 1, textAlign: 'left' }}>{i.label}</span>
                        {i.countKey && counts[i.countKey] !== undefined && (
                          <span className="arc-nav-count arc-num">{counts[i.countKey]}</span>
                        )}
                        {/* Planned sections are marked, not hidden — the shape
                            of the product is worth showing, silently missing
                            menu entries are not. */}
                        {i.phase && <span className="arc-nav-soon">soon</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </aside>

      <div className="arc-main">
        <header className="arc-header">
          <div className="arc-row" style={{ gap: 9 }}>
            <span style={{ fontWeight: 600 }}>{t?.name ?? '\u2026'}</span>
            {t && <StatusBadge status={t.status} />}
            <span className="arc-mono arc-subtle">{t?.sip_domain}</span>
          </div>
          <div className="arc-spacer" />
          <span className="arc-subtle" style={{ fontSize: 12 }}>
            {active?.label}
          </span>
        </header>

        <main className="arc-content">
          <div className="arc-page-max">
            {account.error && <ErrorBanner error={account.error} />}
            <Section section={section} tenantId={tenantId} tenant={t ?? null}
              onNavigate={go} onOpenExtension={openExtension} />
          </div>
        </main>
      </div>
    </div>
  );
}

function Section({ section, tenantId, tenant, onNavigate, onOpenExtension }: {
  section: string; tenantId: string; tenant: import('@arcnet/contracts').Tenant | null;
  onNavigate: (key: string) => void;
  onOpenExtension: (id: string) => void;
}) {
  switch (section) {
    case 'dashboard':
      return <AccountDashboard tenantId={tenantId} tenant={tenant} onNavigate={onNavigate} />;
    case 'extensions':   return <ExtensionsPage tenantId={tenantId} embedded />;
    case 'portal-users': return <UsersTab tenantId={tenantId} />;
    case 'ring-groups':  return <RingGroupsTab tenantId={tenantId} />;
    case 'queues':       return <QueuesTab tenantId={tenantId} />;
    case 'ivrs':         return <IvrsTab tenantId={tenantId} />;
    case 'schedules':   return <SchedulesTab tenantId={tenantId} />;
    case 'devices':     return <DevicesTab tenantId={tenantId} />;
    case 'recording':   return <RecordingTab tenantId={tenantId} />;
    case 'restrictions':
    case 'dial-permissions': return <DialPolicyTab tenantId={tenantId} />;
    case 'audit':       return <AuditTab tenantId={tenantId} />;
    case 'voicemail':   return <VoicemailTab tenantId={tenantId} onOpenExtension={onOpenExtension} />;
    case 'forwarding':  return <ForwardingTab tenantId={tenantId} onOpenExtension={onOpenExtension} />;
    case 'inbound':
    case 'numbers':      return <NumbersTab tenantId={tenantId} />;
    case 'trunks':       return <AccountTrunksTab tenantId={tenantId} />;
    case 'calls':        return <CdrsPage tenantId={tenantId} embedded />;
    case 'billing':      return <BillingPage tenantId={tenantId} embedded />;
    case 'profile':      return <ProfileSection tenantId={tenantId} tenant={tenant} />;
    default:             return <NotBuilt section={section} />;
  }
}

function ProfileSection({ tenantId, tenant }: {
  tenantId: string; tenant: import('@arcnet/contracts').Tenant | null;
}) {
  const stats = useCdrStats(tenantId);
  const extensions = useExtensions(tenantId, { limit: 200 });
  const used = extensions.data?.data.length ?? 0;
  const limit = tenant?.max_extensions ?? 0;

  return (
    <>
      <div className="arc-page-head">
        <div>
          <h1 className="arc-h1">Company profile</h1>
          <p className="arc-muted" style={{ marginTop: 3 }}>
            Account identity, limits and usage.
          </p>
        </div>
      </div>

      <div className="arc-grid arc-grid-4" style={{ marginBottom: 14 }}>
        <Stat label="Extensions" value={`${used}${limit ? ` / ${limit}` : ''}`}
          sub={limit && used >= limit ? 'at the limit' : 'in use'}
          tone={limit && used >= limit ? 'warn' : undefined} />
        <Stat label="Calls (30 days)" value={formatNumber(stats.data?.total_calls ?? 0)}
          sub={stats.data ? `${stats.data.answer_rate_pct}% answered` : undefined} />
        <Stat label="Spend (30 days)"
          value={formatMoney(stats.data?.spend ?? null, stats.data?.currency ?? 'ZAR')} />
        <Stat label="Channels" value={tenant?.max_concurrent_calls ?? '\u2014'}
          sub={`${tenant?.max_cps ?? '\u2014'} calls/sec`} />
      </div>

      <div className="arc-card">
        <div className="arc-card-head"><h2 className="arc-h2">Details</h2></div>
        <div className="arc-card-body">
          <dl className="arc-kv">
            <dt>Company name</dt><dd>{tenant?.name ?? '\u2014'}</dd>
            <dt>Slug</dt><dd className="arc-mono">{tenant?.slug ?? '\u2014'}</dd>
            <dt>SIP domain</dt>
            <dd className="arc-mono">
              {tenant?.sip_domain ?? '\u2014'}
              {/* ADR-002/013: HA1 credentials are bound to this realm. */}
              <span className="arc-subtle" style={{ marginLeft: 8 }}>permanent</span>
            </dd>
            <dt>Timezone</dt><dd>{tenant?.timezone ?? '\u2014'}</dd>
            <dt>Country</dt><dd>{tenant?.country_code ?? '\u2014'}</dd>
            <dt>Currency</dt><dd>{tenant?.currency ?? '\u2014'}</dd>
            <dt>Created</dt>
            <dd>{tenant ? new Date(tenant.created_at).toLocaleDateString('en-ZA') : '\u2014'}</dd>
          </dl>
        </div>
      </div>
    </>
  );
}

function NotBuilt({ section }: { section: string }) {
  const item = CONFIG_NAV.flatMap((g) => g.items).find((i) => i.key === section);
  return (
    <>
      <div className="arc-page-head">
        <div><h1 className="arc-h1">{item?.label ?? section}</h1></div>
      </div>
      <div className="arc-card">
        <Empty
          icon="settings"
          title={`${item?.label ?? section} is not built yet`}
          hint={`Planned for ${item?.phase ?? 'a later phase'}. The entry is here so the shape of the product is visible — there is no backend behind it yet.`}
        />
      </div>
    </>
  );
}
